<template>
  <div>
    <!-- Error and Information messages -->
    <div v-if="serverError" class="card-panel amber lighten-2" style="padding: 15px;">
      <span class="material-icons" style="vertical-align: middle;">error</span>
      <span style="padding-left: 10px;">{{ $t('views.switch.alarmsettings.errorgettingdata') }}: {{ serverError }}</span>
    </div>

    <!-- Activate algorithm modal -->
    <div v-if="showActivateAlgorithmModal" class="modal-overlay" @click.self="cancelActivateAlgorithmSelectionModal">
      <div class="modal-content">
        <h4>{{ $t('views.switch.alarmsettings.activatealgorithm') }}</h4>
        <p>{{ $t('views.switch.alarmsettings.selectsections') }}:</p>

        <!-- Checkbox list -->
        <div v-for="(section, index) in sections" :key="index" class="checkbox-item">
          <label>
            <input
              type="checkbox"
              :checked="isTempSelected(section)"
              @change="toggleTempSelection(section)"
            />
            <span>{{ section.name }}</span>
          </label>
        </div>

        <!-- Warning message -->
        <p v-if="tempSelectedSections.length === 0" class="red-text">{{ $t('views.switch.alarmsettings.onesectionselected') }}.</p>

        <div class="modal-footer">
          <button
            style="min-width: 105px;"
            class="modal-close waves-effect waves-light btn-small green"
            @click="confirmActivateAlgorithmSelectionModal(selectedAlgorithm)"
            :disabled="tempSelectedSections.length === 0 || activating">
            <span v-if="!activating">{{ $t('views.switch.alarmsettings.activate') }}</span>
            <span v-else>
              <i class="material-icons spin">autorenew</i>
            </span>
          </button>
          <button class="modal-close waves-effect waves-red btn-flat"
            @click="cancelActivateAlgorithmSelectionModal"
            :disabled="activating">
            {{ $t('views.switch.alarmsettings.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit sections modal -->
    <div v-if="showEditSectionsModal" class="modal-overlay" @click.self="cancelEditSectionsModal">
      <div class="modal-content">
        <h4>{{ $t('views.switch.alarmsettings.editsections') }}</h4>
        <p>{{ $t('views.switch.alarmsettings.selectsections') }}:</p>

        <!-- Checkbox list -->
        <div v-for="(section, index) in sections" :key="index" class="checkbox-item">
          <label>
            <input
              type="checkbox"
              :checked="isTempSelected(section)"
              @change="toggleTempSelection(section)"
            />
            <span>{{ section.name }}</span>
          </label>
        </div>

        <!-- Warning message -->
        <p v-if="tempSelectedSections.length === 0" class="red-text">{{ $t('views.switch.alarmsettings.onesectionselected') }}.</p>

        <div class="modal-footer">
          <button
            style="min-width: 105px;"
            class="modal-close waves-effect waves-light btn-small green"
            @click="confirmEditSectionsModal"
            :disabled="tempSelectedSections.length === 0 || activating">
            <span v-if="!activating">{{ $t('views.switch.alarmsettings.save') }}</span>
            <span v-else>
              <i class="material-icons spin">autorenew</i>
            </span>
          </button>
          <button class="modal-close waves-effect waves-red btn-flat"
            @click="cancelEditSectionsModal"
            :disabled="activating">
              {{ $t('views.switch.alarmsettings.cancel') }}
            </button>
        </div>
      </div>
    </div>

    <!-- Warning modal for direction change -->
    <div v-if="showConfirmationModal" class="modal-overlay" @click.self="cancelDirectionChange">
      <div class="modal-content">
        <h4>{{ $t('warning') }}</h4>
        <p>{{ $t('views.switch.alarmsettings.modalwarningdirection') }}</p>
        <div class="modal-footer">
          <button class="modal-close waves-effect waves-green btn-flat" @click="confirmDirectionChange">{{ $t('yes') }}</button>
          <button class="modal-close waves-effect waves-red btn-flat" @click="cancelDirectionChange">{{ $t('no') }}</button>
        </div>
      </div>
    </div>

    <!-- 'Select a motor' message -->
    <div v-if="selectedAsset === asset" class="row">
      <div class="col s12">
        {{ $t('views.switch.alarmsettings.selectmotor') }}.
      </div>
    </div>

    <!-- List of available algorithms for asset, show only when motor is selected -->
    <div v-else class="row" style="margin-bottom: 10px;">
      <div v-if="!(loadAlgorithms || loading)">

        <!-- List of algorithms -->
        <div class="col s3" style="min-width: 350px;">
          <div class="dropdown-container" @focusout="handleFocusOut" tabindex="-1">
            <span style="margin-right: 10px;"><b>{{ $t('views.switch.alarmsettings.algorithm') }}:</b></span>
            <div class="custom-select" @blur="open = false">
              <div class="selected" :class="{ open: open }" @click.stop="toggleOpen">
                {{ selected ? selected.printName : $t('views.switch.alarmsettings.selectanalgorithm') }}
                <span v-if="selected && selected.active" class="new badge" :data-badge-caption="$t('views.switch.alarmsettings.active')"></span>
                <span v-if="selected && selected.active" class="new badge blue" :data-badge-caption="$t(`views.switch.alarmsettings.${selected.direction.toLowerCase()}`)"></span>
              </div>
              <div class="items" :class="{ selectHide: !open }">
                <div
                  v-for="(algorithm, i) in algorithms"
                  :key="i"
                  @click.stop="selectAlgorithmAsync(algorithm)"
                  class="item-option"
                >
                  {{ algorithm.printName }}
                  <span v-if="algorithm.active" class="new badge" :data-badge-caption="$t('views.switch.alarmsettings.active')"></span>
                  <span v-if="algorithm.active" class="new badge blue" :data-badge-caption="$t(`views.switch.alarmsettings.${algorithm.direction.toLowerCase()}`)"></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Activate/deactivate button and sections edit button -->
        <div v-if="selectedAlgorithm" class="col s1 dropdown-container" style="min-width: 250px;">
          <a v-if="!selectedAlgorithm.active" style="margin-right: 5px; margin-bottom: 5px;
            margin-top: 8px; min-width: 105px;" class="waves-effect waves-light btn-small green"
            @click="openActivateAlgorithmModal" :disabled="systemRole === 1">
              {{ $t('views.switch.alarmsettings.activate') }}
          </a>
          <a v-else
            style="margin-right: 5px; display: flex; justify-content: center; align-items: center;
            margin-bottom: 5px; margin-top: 8px; min-width: 105px;"
            class="waves-effect waves-light btn-small red"
            @click="deactivateAlgorithmAsync"
            :disabled="systemRole === 1 || loadAlgorithmParameters">
            <span v-if="!deactivating">
              {{ $t('views.switch.alarmsettings.deactivate') }}
            </span>
            <span v-else>
              <i class="material-icons spin">autorenew</i>
            </span>
          </a>

          <a v-if="selectedAlgorithm.active" style="margin-right: 5px; margin-bottom: 5px;
            margin-top: 8px; min-width: 105px;" class="waves-effect waves-light btn-small purple"
            @click="openEditSectionsModal" :disabled="systemRole === 1 || loadAlgorithmParameters">
              {{ $t('views.switch.tabs.sections') }}
            </a>
        </div>

        <!-- Direction selection -->
        <div class="col s1 dropdown-container" style="min-width: 250px;">
          <span style="margin-right: 10px;"><b>{{ $t('views.switch.alarmsettings.direction') }}:</b></span>
          <span>
            <select
              v-model="newDirection"
              class="select-s1 custom-select browser-default"
              @change="handleDirectionChange"
              :disabled="!selectedAlgorithm"
              style="width: 140px;">
              <option
                v-for="(value) in directions"
                :key="value.id"
                :value="value.id"
                :style="{ backgroundColor: disableBoth && value.id === 'Both' ? '#e0e0e0' : '#ffffff', color: ((disableBoth && value.id === 'Both') || (disableNormal && value.id === 'Normal') || (disableReverse && value.id === 'Reverse')) ? '#a9a9a9' : '#000000' }"
                :disabled="(disableBoth && value.id === 'Both') || (disableNormal && value.id === 'Normal') || (disableReverse && value.id === 'Reverse')" >
                {{value.name}}
              </option>
            </select>
          </span>
        </div>

        <!-- Graph mode -->
        <div class="col s1 dropdown-container" style="min-width: 250px;">
          <span style="margin-right: 10px;"><b>{{ $t('views.switch.alarmsettings.graph') }}:</b></span>
          <span>
            <select
              v-model="graphMode"
              class="select-s1 custom-select browser-default"
              :disabled="!selectedAlgorithm"
              style="width: 160px;">
              <option
                key="1"
                value="scatterplot"
                > {{ $t('views.switch.alarmsettings.scatterplot') }}
              </option>
              <option
                key="2"
                value="trendplot"
                > {{ $t('views.switch.alarmsettings.trendplot') }}
              </option>
            </select>
          </span>
        </div>

        <!-- Asset type -->
        <div class="col s3">
          <div v-if="assetWithAlgorithms" class="dropdown-container" style="padding-top: 12px;">
            <span style="margin-right: 10px;"><b>{{ $t('views.switch.alarmsettings.assettype') }}:</b></span>
            <span>{{ assetWithAlgorithms && assetWithAlgorithms.assetType && assetWithAlgorithms.assetType.printName || 'N/A' }}</span>
          </div>
        </div>

      </div>
      <div v-else>
        {{ $t('views.switch.alarmsettings.loading') }}...
      </div>
    </div>

    <!-- Panel with algorithm parameters and preview graph -->
    <div class="row white" style="display: flex;" v-if="algorithmParameters && selectedAlgorithm && selectedAlgorithm.active">
      <!-- List of algorithm parameters which belong to selected parameter -->
      <div class="col s3 z-depth-1 white" style="min-width: 350px;">
        <div v-if="!loadAlgorithmParameters && algorithmParameters.length > 0" style="display: flex; flex-direction: column; height: 580px; overflow-y: auto;">
          <!-- Scrollable table container -->
          <div style="flex: 1; ">
            <div v-if="information" class="card-panel cyan lighten-2" style="padding: 15px;">
              <span class="material-icons" style="vertical-align: middle;">info</span>
              <span style="padding-left: 10px;">{{ information }}</span>
            </div>
            <table class="algorithms" style="">
              <thead>
                <tr>
                  <th>{{ $t('views.switch.alarmsettings.parameter') }}</th>
                  <th>{{ $t('views.switch.alarmsettings.value') }}</th>
                </tr>
              </thead>
              <tbody>

                <!-- Select section-->
                <tr data-toggle="tooltip" :title="$t('views.switch.alarmsettings.selectsection')">
                  <td class="algorithmListItem">{{$t('views.switch.alarmsettings.sectie')}}</td>
                  <td class="algorithmListItem">
                    <template>
                      <select v-model="selectedSection" class="algorithmListItem dropdown-container-small" style="width: 175px;">
                        <option v-for="section in selectedSections" :key="section.id" :value="section.sectionType">
                          {{ section.name }}
                        </option>
                      </select>
                    </template>
                  </td>
                </tr>

                <!-- Start date -->
                <tr data-toggle="tooltip" :title="$t('views.switch.alarmsettings.measurementsincluded')">
                  <td class="algorithmListItem">{{ $t('views.switch.alarmsettings.startdate') }}</td>
                  <td class="algorithmListItem">
                    <template>
                      <v-date-picker v-model="startDate" style="position: relative;" popover-align="right">
                        <input class="algorithmListItem" style="size: 13px; width: 80px; height: 25px;" :value="formatDate(startDate)">
                      </v-date-picker>
                    </template>
                  </td>
                </tr>

                <!-- All other parameters which belong to selected algorithm -->
                <tr v-for="parameter in filteredAlgorithmParameters" :key="parameter.id" data-toggle="tooltip" :title="parameter.description">
                  <td class="algorithmListItem">{{ parameter.printName }}</td>
                  <td class="algorithmListItem">
                    <input type="number"
                      v-model="parameter.parameterValue"
                      style="font-size: 13px; width: 60px; height: 25px;"
                      :min="parameter.minValue"
                      :max="parameter.maxValue"
                      :step="parameter.stepSize"
                      :placeholder="parameter.nullable ? 'empty' : ''"
                      @blur="validateInput(parameter)"
                      @input="handleInput(parameter, $event)"
                    >
                    <i v-if="parameterDifferences[parameter.parameterName]"
                      style="margin-left: 2px; margin-top: 4px; color: orange;"
                      class="material-icons right"
                      data-toggle="tooltip"
                      :title="$t('views.switch.alarmsettings.parameterdiffersfor') + ' ' + oppositeDirection + ' ' + $t('views.switch.alarmsettings.directionvalue') + ': ' + (selectedDirection === 'Reverse' ? parameterDifferences[parameter.parameterName].currentValue : parameterDifferences[parameter.parameterName].otherValue)">
                      report
                    </i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else-if="loadAlgorithmParameters">
          {{ $t('views.switch.alarmsettings.loading') }}...
        </div>
        <div v-else style="display: flex; justify-content: center; align-items: center; height: 50%;">
          {{ $t('views.switch.alarmsettings.noalgorithmparameters') }}.
        </div>

        <!-- Flex container for buttons -->
        <div style="display: flex; justify-content:center; align-items: center; margin: 10px; padding: 5px;">
          <a class="waves-effect waves-light btn-small orange" style="min-width: 75px; margin: 5px;" @click="loadDefaultParametersAsync()" :disabled="systemRole === 1"
            data-toggle="tooltip" :title="$t('views.switch.alarmsettings.defaults')">
            <i style="margin-left: 2px;" class="material-icons replay">replay</i>
          </a>
          <a class="waves-effect waves-light btn-small grey" style="min-width: 75px; margin: 5px;" @click="cancelAlgorithmParametersAsync()" :disabled="systemRole === 1"
            data-toggle="tooltip" :title="$t('views.switch.alarmsettings.cancel')">
            <i style="margin-left: 2px;" class="material-icons cancel">cancel</i>
          </a>
          <a class="waves-effect waves-light btn-small green" style="min-width: 75px; margin: 5px;" @click="saveAlgorithmParametersAsync(selectedAlgorithm)" :disabled="systemRole === 1"
            data-toggle="tooltip" :title="$t('views.switch.alarmsettings.save')">
            <i style="margin-left: 2px;" class="material-icons save">save</i>
            <i v-if="unsavedParameterChanges" style="margin-left: 2px;" class="material-icons right">warning</i>
          </a>
          <a class="waves-effect waves-light btn-small blue right" style="min-width: 75px; margin: 5px;" @click="updatePreview"
            data-toggle="tooltip" :title="$t('views.switch.alarmsettings.preview')">
            <i style="margin-left: 2px;" class="material-icons visibility">visibility</i>
          </a>
          <a
            class="waves-effect waves-light btn-small purple right"
            :class="{ purple: ignoreOutliers, grey: !ignoreOutliers }"
            style="min-width: 25px; margin: 5px;"
            @click="toggleIgnoreOutliers"
            data-toggle="tooltip"
            :title="$t('views.switch.alarmsettings.ignoreoutliers')"
          >
            <i style="margin-left: 0px;" class="material-icons filter">filter</i>
          </a>
        </div>
      </div>

      <!-- Preview graph -->
      <div class="col s9 z-depth-1 white" >
        <div v-if="!loadPreview" >
          <alarm-preview-scatter v-if="graphMode==='scatterplot'"
            :refreshPreview="refreshPreview"
            :asset="selectedAsset"
            :r8token="r8token"
            :assetAlgorithm="normalAlgorithm"
            :startDate="startDate"
            :unitOfMeasure="this.selectedAlgorithm.unitOfMeasure || unitOfMeasure"
            :graphMode="graphMode"
            :sectionType="selectedSection"
            :direction="false"
            :ignoreOutliers="ignoreOutliers"
            @point-selected="pointSelected"
            @information="setInformation"
            @error="setError">
          </alarm-preview-scatter>
          <alarm-preview-trend v-if="graphMode==='trendplot'"
            :refreshPreview="refreshPreview"
            :asset="selectedAsset"
            :r8token="r8token"
            :assetAlgorithm="normalAlgorithm"
            :startDate="startDate"
            :unitOfMeasure="this.selectedAlgorithm.unitOfMeasure || unitOfMeasure"
            :graphMode="graphMode"
            :sectionType="selectedSection"
            :direction="false"
            :ignoreOutliers="ignoreOutliers"
            @point-selected="pointSelected"
            @information="setInformation"
            @error="setError">
          </alarm-preview-trend>
          <alarm-plot
            :samples="samples"
            :color="color"
            :direction="plotDirection"
            :areaRangeData="areaRangeData"
            :title="title"
            :unitOfMeasure="unitOfMeasure">
          </alarm-plot>
          <alarm-preview-scatter v-if="graphMode==='scatterplot'"
            :refreshPreview="refreshPreview"
            :asset="selectedAsset"
            :r8token="r8token"
            :assetAlgorithm="reverseAlgorithm"
            :startDate="startDate"
            :unitOfMeasure="this.selectedAlgorithm.unitOfMeasure || unitOfMeasure"
            :graphMode="graphMode"
            :sectionType="selectedSection"
            :direction="true"
            :ignoreOutliers="ignoreOutliers"
            @point-selected="pointSelected"
            @information="setInformation"
            @error="setError">
          </alarm-preview-scatter>
          <alarm-preview-trend v-if="graphMode==='trendplot'"
            :refreshPreview="refreshPreview"
            :asset="selectedAsset"
            :r8token="r8token"
            :assetAlgorithm="reverseAlgorithm"
            :startDate="startDate"
            :unitOfMeasure="this.selectedAlgorithm.unitOfMeasure || unitOfMeasure"
            :graphMode="graphMode"
            :sectionType="selectedSection"
            :direction="true"
            :ignoreOutliers="ignoreOutliers"
            @point-selected="pointSelected"
            @information="setInformation"
            @error="setError">
          </alarm-preview-trend>
        </div>
        <div v-else>
          {{ $t('views.switch.alarmsettings.loading') }}...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import PointAlarmsPrototypeService from '@/services/r8/PointAlarmsPrototypeService'
import DatePicker from '@/components/switch/DatePicker'
import VCalendar from 'v-calendar'
import Translation from '../../i18n/Translation'
import AlarmPreviewScatter from '@/components/switch/AlarmPreviewScatter.vue'
import AlarmPreviewTrend from '@/components/switch/AlarmPreviewTrend.vue'
import AlarmPlot from '@/components/switch/AlarmPlot.vue'
import moment from 'moment'
import AssetsService from '@/services/r8/AssetsService'
import AuthenticationService from '@/services/AuthenticationService'
import AlarmService from '@/services/AlarmService'

Vue.use(VCalendar, {
  locale: Translation.getCurrentLanguage(),
  formats: {
    display: 'DD-MM-YYYY',
    title: 'MMMM YYYY',
    weekdays: 'W',
    navMonths: 'MMM',
    input: ['L', 'YYYY-MM-DD', 'YYYY/MM/DD'],
    dayPopover: 'L'
  }
})
export default {
  components: {
    'datePicker': DatePicker,
    AlarmPreviewScatter,
    AlarmPreviewTrend,
    AlarmPlot
  },
  props: ['asset', 'selectedAsset'],
  name: 'alarmconfiguration',
  data () {
    return {
      loading: false,
      loadAlgorithms: false,
      loadAlgorithmParameters: false,
      loadPreview: false,
      r8token: null,
      serverError: '',
      information: '',
      systemRole: null,
      activating: false,
      deactivating: false,
      permissionLevel: 0,

      directions: [
        { id: 'Normal', name: this.$t('views.switch.alarmsettings.normal') },
        { id: 'Both', name: this.$t('views.switch.alarmsettings.both') },
        { id: 'Reverse', name: this.$t('views.switch.alarmsettings.reverse') }
      ],
      selectedDirection: 'Both',
      previousDirection: 'Both',
      newDirection: 'Both',
      showConfirmationModal: false,
      proceedWithDirectionChange: false,
      disableBoth: false,
      disableNormal: false,
      disableReverse: false,

      selectedAlgorithm: null,

      algorithms: [],

      activeAlgorithms: [],
      availableAlgorithms: [],
      assetWithAlgorithms: null,
      algorithmParameters: null,
      originalAlgorithmParameters: null,
      defaultAlgorithmParameters: null,
      parameterDifferences: {},

      normalAlgorithm: null,
      reverseAlgorithm: null,

      selected: null,
      open: false,

      locale: 'nl-NL',
      startDate: new Date(),
      originalStartDate: new Date(),
      attrs: [
        {
          locale: 'nl-NL'
        }
      ],
      samples: [],
      color: null,
      plotDirection: false,
      title: '',
      areaRangeData: [],
      unsavedParameterChanges: false,
      refreshPreview: false,
      unitOfMeasure: '',
      graphMode: 'scatterplot',
      ignoreOutliers: false,

      // Sections
      showActivateAlgorithmModal: false,
      showEditSectionsModal: false,
      sections: [],
      selectedSections: [],
      tempSelectedSections: [],
      selectedSection: 'TotalMeasurement'
    }
  },
  mounted: async function () {
    this.loading = true
    this.locale = Translation.getCurrentLanguage()
    VCalendar.locale = this.locale
    await this.getR8Token()
    await this.getAlgorithmsAsync()
    this.getSensor()

    // Get current user
    var user = await AuthenticationService.getUser()
    this.systemRole = user.SystemRoleId
    var r8user = await PointAlarmsPrototypeService.getUser()
    this.permissionLevel = r8user.role.permissionLevel

    this.loading = false
  },
  computed: {
    oppositeDirection () {
      if (this.selectedDirection === 'Normal') {
        return this.directions.find(dir => dir.id === 'Reverse').name
      } else if (this.selectedDirection === 'Reverse') {
        return this.directions.find(dir => dir.id === 'Normal').name
      } else {
        return ''
      }
    },
    filteredAlgorithmParameters () {
      return this.algorithmParameters.filter(
        parameter => parameter.minPermissionLevel <= this.permissionLevel
      )
    }
  },
  watch: {
    async selectedAsset () {
      this.loading = true
      this.serverError = null
      this.information = null
      this.selected = null
      this.selectedAlgorithm = null
      this.normalAlgorithm = null
      this.reverseAlgorithm = null
      this.algorithms = []
      await this.getR8Token()
      await this.getAlgorithmsAsync()
      this.getSensor()
      this.loading = false
    },
    algorithmParameters: {
      handler () {
        this.checkUnsavedChanges()
      },
      deep: true
    },
    startDate: {
      handler () {
        this.checkUnsavedChanges()
      },
      deep: true
    },
    async selectedDirection () {
      // Skip watcher if confirmation is pending
      if (!this.proceedWithDirectionChange) {
        return
      }

      // Handle the direction change after confirmation
      if (this.selectedAlgorithm.active) {
        this.loading = true
        this.loadAlgorithmParameters = true
        this.loadPreview = true
        this.serverError = null
        this.information = null
        this.samples = []
        this.areaRangeData = []
        this.getAssetAlgorithmParameters(this.selected)
        if (this.selectedDirection === 'Normal') {
          this.reverseAlgorithm = null
        }
        if (this.selectedDirection === 'Reverse') {
          this.normalAlgorithm = null
        }
        this.updatePreview()

        this.loading = false
        this.loadAlgorithmParameters = false
        this.loadPreview = false
      }

      this.proceedWithDirectionChange = false
    },
    graphMode () {
      this.samples = []
      this.areaRangeData = []
    },
    selectedSection () {
      this.updatePreview()
    }
  },
  methods: {
    async getR8Token () {
      try {
        const result = await AssetsService.getToken(this.selectedAsset)
        this.r8token = result.token
      } catch (error) {
        this.serverError = this.$t('views.switch.alarmsettings.errorfetchingtoken') + ': ' + error
      }
    },

    async getAlgorithmsAsync (force = false) {
      this.loadAlgorithms = true
      this.differences = {}
      this.algorithms = []
      this.activeAlgorithms = []
      this.availableAlgorithms = []
      this.selectedAlgorithm = null
      this.algorithmParameters = null
      this.originalAlgorithmParameters = null
      this.defaultAlgorithmParameters = null
      this.resetSectionsAndDirection()

      try {
        if (this.asset !== this.selectedAsset || force) {
          this.assetWithAlgorithms = await PointAlarmsPrototypeService.getAssetWithAlgorithms(
            this.selectedAsset,
            this.locale,
            this.r8token
          )

          this.startDate = new Date(this.assetWithAlgorithms.startDate)
          this.originalStartDate = new Date(this.assetWithAlgorithms.startDate)

          // Get distinct active algorithms with direction tracking
          this.activeAlgorithms = Object.values(
            this.assetWithAlgorithms.assetAlgorithms.reduce((acc, item) => {
              const key = `${item.algorithm.id}-${item.algorithm.name}`

              if (!acc[key]) {
                acc[key] = { ...item.algorithm, directions: new Set() }
              }

              // Track direction (false = normal, true = reverse)
              acc[key].directions.add(item.direction ? 'reverse' : 'normal')

              return acc
            }, {})
          ).map(algorithm => ({
            ...algorithm,
            direction: algorithm.directions.size === 2 ? 'both' : [...algorithm.directions][0]
          }))

          // Get available algorithms for this asset
          this.availableAlgorithms = await PointAlarmsPrototypeService.getAvailableAlgorithms(
            this.selectedAsset,
            this.locale,
            this.r8token
          )

          if (this.availableAlgorithms && this.availableAlgorithms.assetType) {
            this.availableAlgorithms = this.availableAlgorithms.assetType.assetTypeAlgorithms.map(
              item => item.algorithm
            )
          }

          // Combine available and active algorithms, adding direction information
          if (Array.isArray(this.availableAlgorithms)) {
            this.algorithms = this.availableAlgorithms.map(algorithm => {
              const activeAlgorithm = this.activeAlgorithms.find(active => active.id === algorithm.id)

              return {
                ...algorithm,
                active: !!activeAlgorithm,
                direction: activeAlgorithm ? activeAlgorithm.direction : null // 'normal', 'reverse', or 'both'
              }
            })
          } else {
            this.setInformation(this.$t('views.switch.alarmsettings.noalgorithms'))
          }

          // Ensure all active algorithms are included, even if not in availableAlgorithms
          this.activeAlgorithms.forEach(activeAlgorithm => {
            if (!this.algorithms.some(algorithm => algorithm.id === activeAlgorithm.id)) {
              this.algorithms.push({
                ...activeAlgorithm,
                active: true
              })
            }
          })

          // Sort alphabetically
          this.algorithms.sort((a, b) => a.name.localeCompare(b.name))

          this.unsavedParameterChanges = false
        }
      } catch (err) {
        this.serverError = err
      } finally {
        this.loadAlgorithms = false
      }
    },

    getSensor () {
      AlarmService.GetSensorsByRailobjectId(this.selectedAsset.RailObjectId).then(result => {
        const sensor = result.data.find(s => s.RailObjectId === this.selectedAsset.RailObjectId)
        if (sensor) {
          this.unitOfMeasure = sensor.ConfigSensor.UnitOfMeasure
        }
      })
    },

    async getDefaultAlgorithmParametersAsync (algorithm) {
      this.defaultAlgorithmParameters = await PointAlarmsPrototypeService.getAlgorithmParametersById(this.algorithms.find(a => a.id === algorithm.id), this.r8token)
      this.defaultAlgorithmParameters = this.defaultAlgorithmParameters.algorithmParameters.sort((a, b) => a.displayOrder - b.displayOrder)
    },

    getAssetAlgorithmParameters (algorithm) {
      this.algorithmParameters = null
      var assetAlgorithmNormal = this.assetWithAlgorithms.assetAlgorithms.find(x => x.algorithm.id === algorithm.id && x.direction === false)
      if (assetAlgorithmNormal === undefined) {
        this.normalAlgorithm = null
      } else {
        this.normalAlgorithm = assetAlgorithmNormal
      }

      var assetAlgorithmReverse = this.assetWithAlgorithms.assetAlgorithms.find(x => x.algorithm.id === algorithm.id && x.direction === true)
      if (assetAlgorithmReverse === undefined) {
        this.reverseAlgorithm = null
      } else {
        this.reverseAlgorithm = assetAlgorithmReverse
      }

      if ((this.selectedDirection === 'Normal' && assetAlgorithmNormal !== undefined) || (this.selectedDirection === 'Both' && assetAlgorithmNormal !== undefined)) {
        this.algorithmParameters = assetAlgorithmNormal.assetAlgorithmParameters.sort((a, b) => a.displayOrder - b.displayOrder)
        this.originalAlgorithmParameters = JSON.parse(JSON.stringify(assetAlgorithmNormal.assetAlgorithmParameters.sort((a, b) => a.displayOrder - b.displayOrder)))
      } else if ((this.selectedDirection === 'Reverse' && assetAlgorithmReverse !== undefined) || (this.selectedDirection === 'Both' && assetAlgorithmReverse !== undefined)) {
        this.algorithmParameters = assetAlgorithmReverse.assetAlgorithmParameters.sort((a, b) => a.displayOrder - b.displayOrder)
        this.originalAlgorithmParameters = JSON.parse(JSON.stringify(assetAlgorithmReverse.assetAlgorithmParameters.sort((a, b) => a.displayOrder - b.displayOrder)))
      } else {
        this.setError(`Cannot find suitable algorithm parameters for direction ${this.selectedDirection}.`)
      }
    },

    toggleOpen () {
      this.open = !this.open
    },

    handleFocusOut () {
      this.open = false
    },

    formatDate (date) {
      return moment(date).format('DD-MM-YYYY')
    },

    handleInput (parameter, event) {
      let value = event.target.value

      // Allow empty input while typing
      if (value === '' || value === '-') {
        parameter.parameterValue = value
        return
      }

      // Ensure it's a valid number
      let num = parseFloat(value)

      // Ensure value is within range
      if (!isNaN(num)) {
        if (num < parameter.minValue) {
          num = parameter.minValue
        } else if (num > parameter.maxValue) {
          num = parameter.maxValue
        }

        // Assign string value back to v-model to prevent Vue from forcing number conversion
        parameter.parameterValue = value
      } else {
        // If invalid, reset to last valid value
        parameter.parameterValue = parameter.parameterValue || ''
      }

      // Synchronize the updated parameter value with the opposite direction
      if (this.selectedDirection === 'Both') {
        this.updateBothDirections(parameter)
      }

      // Make sure to show differences between directions immediately
      const differences = this.compareAlgorithmDirections(this.selectedAlgorithm)
      this.parameterDifferences = differences || {}
    },

    updateBothDirections (parameter) {
      // Update the normal direction parameter
      const normalParam = this.normalAlgorithm.assetAlgorithmParameters.find(
        p => p.parameterName === parameter.parameterName
      )
      if (normalParam) {
        normalParam.parameterValue = parameter.parameterValue
      }

      // Update the reverse direction parameter
      const reverseParam = this.reverseAlgorithm.assetAlgorithmParameters.find(
        p => p.parameterName === parameter.parameterName
      )
      if (reverseParam) {
        reverseParam.parameterValue = parameter.parameterValue
      }
    },

    validateInput (parameter) {
      // If the field is empty and nullable is false, reset to minValue
      if (parameter.parameterValue === '' && !parameter.nullable) {
        parameter.parameterValue = parseFloat(parameter.minValue)
        return
      }

      let value = parseFloat(parameter.parameterValue)

      if (!parameter.nullable && isNaN(value)) {
        parameter.parameterValue = parseFloat(parameter.minValue)
      } else {
        if (value < parseFloat(parameter.minValue)) {
          value = parseFloat(parameter.minValue)
        } else if (value > parseFloat(parameter.maxValue)) {
          value = parseFloat(parameter.maxValue)
        }

        // Round value for int type
        if (parameter.type === 'int') {
          parameter.parameterValue = Math.round(value).toString()
        } else {
          parameter.parameterValue = value.toString()
        }
      }

      if (parameter.type === 'int') {
        if (typeof parameter.parameterValue === 'string') {
          parameter.parameterValue = parameter.parameterValue.replace(/[^\d-]/g, '')
          if (parameter.parameterValue.includes('-') && parameter.parameterValue.indexOf('-') !== 0) {
            parameter.parameterValue = parameter.parameterValue.replace(/-/g, '')
          }
        }
      } else if (parameter.type === 'double') {
        if (typeof parameter.parameterValue === 'string') {
          parameter.parameterValue = parameter.parameterValue.replace(/[^\d.-]/g, '')
          if (parameter.parameterValue.includes('-') && parameter.parameterValue.indexOf('-') !== 0) {
            parameter.parameterValue = parameter.parameterValue.replace(/-/g, '')
          }
          const parts = parameter.parameterValue.split('.')
          if (parts.length > 2) {
            parameter.parameterValue = parts[0] + '.' + parts.slice(1).join('')
          }
        }
      }
    },

    compareAlgorithmDirections (selectedAlgorithm) {
      if (selectedAlgorithm && this.assetWithAlgorithms) {
        const d = this.assetWithAlgorithms.assetAlgorithms.filter(
          (x) => x.algorithm.id === selectedAlgorithm.id
        )
        if (d.length === 2) {
          const params1 = d[0].assetAlgorithmParameters.reduce((acc, param) => {
            acc[param.parameterName] = param.parameterValue
            return acc
          }, {})

          const params2 = d[1].assetAlgorithmParameters.reduce((acc, param) => {
            acc[param.parameterName] = param.parameterValue
            return acc
          }, {})

          const differences = {}

          // Check for differences in params1 vs params2
          for (let key in params1) {
            if (params1[key] !== params2[key]) {
              differences[key] = {
                currentValue: params1[key], // Current direction
                otherValue: params2[key]    // Other direction
              }
            }
          }

          return differences
        } else {
          console.log('We expect 2 algorithms, one for each direction!')
          return null
        }
      } else {
        console.log('No active algorithm')
        return null
      }
    },

    async selectAlgorithmAsync (value, force = false) {
      this.selected = value
      this.open = false
      this.serverError = null
      this.information = null
      this.samples = []
      this.areaRangeData = []
      this.loadAlgorithmParameters = true
      this.loadPreview = true

      try {
        if (value !== this.selectedAlgorithm || force) {
          this.selectedAlgorithm = value

          // Set direction options accordingly
          this.setDirectionMenu(this.selectedAlgorithm.direction)

          // Load algorithm parameters for selected algorithm when algorithm is activated
          if (this.selectedAlgorithm.active) {
            this.assetWithAlgorithms = await PointAlarmsPrototypeService.getAssetWithAlgorithms(this.selectedAsset, this.locale, this.r8token)
            const differences = this.compareAlgorithmDirections(this.selectedAlgorithm)
            this.parameterDifferences = differences || {}

            let identical = Object.keys(this.parameterDifferences).length === 0
            if (!identical) {
              this.setInformation(this.$t('views.switch.alarmsettings.parametersdiffer'))
            }

            this.getAssetAlgorithmParameters(value)

            // Determine default graph mode based on available algorithm parameters
            const thresholdNames = ['AlarmMultiplierUp', 'WarningMultiplierUp', 'WarningMultiplierDown', 'AlarmMultiplierDown']
            const hasThresholdParameters = this.algorithmParameters.some(parameter =>
              thresholdNames.includes(parameter.parameterName)
            )
            if (hasThresholdParameters) {
              this.graphMode = 'scatterplot'
            } else {
              this.graphMode = 'trendplot'
            }

            // Update preview only when not forced to update
            if (!force) {
              this.updatePreview()
            }
          }

          // Determine direction and get activated sections for current assetAlgorithm
          let dir = false
          if (this.selectedDirection === 'Reverse') {
            dir = true
          }
          let assetAlgorithms = this.assetWithAlgorithms.assetAlgorithms.find(
            aa => aa.algorithmId === this.selectedAlgorithm.id && aa.direction === dir
          )
          let activatedSections = []
          if (assetAlgorithms) {
            activatedSections = assetAlgorithms.assetAlgorithmSections
          }

          // Create intersection between assetSections and algorithmSections
          this.sections = []
          this.selectedSections = []
          this.tempSelectedSections = []

          if (this.selectedAlgorithm !== null) {
            // Ensure we always have a TotalMeasurement section for our asset, so it is displayed when our algorithm has TotalMeasurement aswell
            if (!this.assetWithAlgorithms.assetSections.some(as => as.sectionType === 'TotalMeasurement')) {
              this.assetWithAlgorithms.assetSections.push({
                id: null,
                assetId: null,
                asset: null,
                sectionType: 'TotalMeasurement',
                direction: null,
                startTime: null,
                endTime: null
              })
            }
            const assetSectionTypes = this.assetWithAlgorithms.assetSections.map(as => as.sectionType)

            // Sort algorithmSections by displayOrder
            const sortedAlgorithmSections = this.selectedAlgorithm.algorithmSections.sort((a, b) => a.displayOrder - b.displayOrder)
            sortedAlgorithmSections.forEach(as => {
              if (assetSectionTypes.includes(as.sectionType)) {
                this.sections.push({ name: as.printName, value: as.sectionType, sectionType: as.sectionType })

                // Ensure section is preselected when isDefault is true
                if (!this.selectedAlgorithm.active && as.isDefault) {
                  this.selectedSections.push({
                    id: as.id,
                    sectionType: as.sectionType,
                    name: as.printName,
                    isDefault: as.isDefault
                  })
                }

                if (this.selectedAlgorithm.active && activatedSections.some(active => active.sectionType === as.sectionType)) {
                  this.selectedSections.push({
                    id: as.id,
                    sectionType: as.sectionType,
                    name: as.printName,
                    isDefault: as.isDefault
                  })
                }
              }
            })
          }

          // Select default for preview
          if (this.selectedSections.length > 0) {
            let defaultValue = this.selectedSections.find(section => section.isDefault)
            if (defaultValue) {
              this.selectedSection = defaultValue.sectionType
            } else {
              this.selectedSection = this.selectedSections[0].sectionType
            }
          }
        }
      } catch (err) {
        this.setError('Error: ' + err.message)
      } finally {
        this.loadAlgorithmParameters = false
        this.loadPreview = false
      }
    },

    async loadDefaultParametersAsync () {
      await this.getDefaultAlgorithmParametersAsync(this.selectedAlgorithm, this.r8token)
      this.defaultAlgorithmParameters.forEach(p => {
        var parameter = this.algorithmParameters.find(x => x.algorithmParameterId === p.id)
        if (parameter) {
          parameter.parameterValue = p.defaultValue
        }
      })
    },

    setDirectionMenu (direction) {
      if (direction && direction.toLowerCase() === 'normal') {
        this.disableBoth = true
        this.disableNormal = false
        this.disableReverse = true
        this.selectedDirection = 'Normal'
        this.newDirection = 'Normal'
      } else if (direction && direction.toLowerCase() === 'reverse') {
        this.disableBoth = true
        this.disableNormal = true
        this.disableReverse = false
        this.selectedDirection = 'Reverse'
        this.newDirection = 'Reverse'
      } else {
        this.disableBoth = false
        this.disableNormal = false
        this.disableReverse = false
        this.selectedDirection = 'Both'
        this.newDirection = 'Both'
      }
    },

    async activateAlgorithmAsync (algorithm) {
      try {
        // Determine for how many directions we have to activate the algorithms
        var selectedDirections = []
        if (this.selectedDirection === 'Both') {
          selectedDirections.push('Normal')
          selectedDirections.push('Reverse')
        } else {
          selectedDirections.push(this.selectedDirection)
        }

        // Gather default parameters and their values for this algorithm
        await this.getDefaultAlgorithmParametersAsync(algorithm, this.r8token)

        // Create the individual assetAlgorithms for each direction
        for (const d of selectedDirections) {
          let sectionTypes = []
          this.selectedSections.forEach(s => {
            sectionTypes.push(
              s.sectionType
            )
          })
          await PointAlarmsPrototypeService.createAlgorithmSetting(this.selectedAsset, this.r8token, {
            direction: (d === 'Reverse'),
            algorithmId: algorithm.id,
            sectionTypes: sectionTypes
          })
        }

        this.selectedSection = this.selectedSections[0].sectionType

        // Refresh the updated assetAlgorithms
        this.assetWithAlgorithms = await PointAlarmsPrototypeService.getAssetWithAlgorithms(this.selectedAsset, this.locale, this.r8token)
        this.getAssetAlgorithmParameters(algorithm)

        // Also update the dropdown menu
        this.selectedAlgorithm.active = true
        this.selectedAlgorithm.direction = this.selectedDirection
        this.setDirectionMenu(this.selectedAlgorithm.direction)

        this.$notify({
          group: 'alerts',
          type: 'success',
          title: this.$t('views.switch.alarmsettings.algorithmactivated'),
          duration: 1000,
          speed: 1000
        })
      } catch (err) {
        console.log(err)
        this.$notify({
          group: 'alerts',
          type: 'error',
          title: this.$t('views.switch.alarmsettings.algorithmnotactivated'),
          text: '' + err.response.data.title,
          duration: 1000,
          speed: 1000
        })
      }
    },

    async deactivateAlgorithmAsync () {
      this.deactivating = true

      try {
        await PointAlarmsPrototypeService.removeAlgorithm(this.selectedAsset, this.r8token, this.selectedAlgorithm.id)
        this.selectedAlgorithm = null
        this.selected = null
        this.algorithmParameters = null
        this.normalAlgorithm = null
        this.reverseAlgorithm = null
        this.parameterDifferences = {}
        this.samples = []
        this.areaRangeData = []
        this.unsavedParameterChanges = false
        this.selectedDirection = 'Both'

        await this.getAlgorithmsAsync(true)

        this.$notify({
          group: 'alerts',
          type: 'success',
          title: this.$t('views.switch.alarmsettings.algorithmdeactivated'),
          duration: 1000,
          speed: 1000
        })
      } catch (err) {
        console.log(err)
        this.$notify({
          group: 'alerts',
          type: 'error',
          title: this.$t('views.switch.alarmsettings.algorithmnotdeactivated'),
          text: '' + err.response.data,
          duration: 1000,
          speed: 1000
        })
      } finally {
        this.deactivating = false
      }
    },

    async cancelAlgorithmParametersAsync () {
      this.startDate = this.originalStartDate

      // Reset algorithmParameters to the original values
      this.algorithmParameters = JSON.parse(JSON.stringify(this.originalAlgorithmParameters.sort((a, b) => a.displayOrder - b.displayOrder)))

      // Update each parameter's value from originalAlgorithmParameters
      this.originalAlgorithmParameters.forEach(p => {
        const parameter = this.algorithmParameters.find(x => x.algorithmParameterId === p.algorithmParameterId)
        if (parameter) {
          parameter.parameterValue = p.parameterValue
        }
      })

      // Update the assetAlgorithmParameters of normalAlgorithm and reverseAlgorithm directly
      if (this.normalAlgorithm) {
        this.normalAlgorithm.assetAlgorithmParameters = this.algorithmParameters
      }
      if (this.reverseAlgorithm) {
        this.reverseAlgorithm.assetAlgorithmParameters = this.algorithmParameters
      }

      // Mark changes as saved
      this.unsavedParameterChanges = false
    },

    async saveAlgorithmParametersAsync (algorithm) {
      try {
        if (this.unsavedParameterChanges) {
          // Determine directions
          let selectedDirections = []
          if (this.selectedDirection === 'Both') {
            selectedDirections.push('Normal', 'Reverse')

            // Copy parameter values from direction normal to direction reverse if direction both is selected
            const assetAlgorithmNormal = this.assetWithAlgorithms.assetAlgorithms.find(
              x => x.algorithm.id === algorithm.id && x.direction === false
            )
            if (!assetAlgorithmNormal) throw new Error('Algorithm for direction normal not found')

            const assetAlgorithmReverse = this.assetWithAlgorithms.assetAlgorithms.find(
              x => x.algorithm.id === algorithm.id && x.direction === true
            )
            if (!assetAlgorithmReverse) throw new Error('Algorithm for direction reverse not found')

            // Duplicate parameter values from normal to reverse
            assetAlgorithmNormal.assetAlgorithmParameters.forEach(aap => {
              const parReverse = assetAlgorithmReverse.assetAlgorithmParameters.find(x => x.parameterName === aap.parameterName)
              if (parReverse) {
                parReverse.parameterValue = aap.parameterValue
              } else {
                console.log(`Cannot duplicate parameter ${aap.parameterName}, parameter is missing for direction reverse`)
              }
            })
          } else {
            selectedDirections.push(this.selectedDirection)
          }

          for (const d of selectedDirections) {
            // Get current assetAlgorithm id
            const currentAssetAlgorithm = this.assetWithAlgorithms.assetAlgorithms.find(
              x => x.algorithm.id === algorithm.id && x.direction === (d === 'Reverse')
            )
            if (!currentAssetAlgorithm) throw new Error('Algorithm not found')

            await PointAlarmsPrototypeService.updateAlgorithmSetting(this.selectedAsset, this.r8token, currentAssetAlgorithm)
          }

          // Update originalAlgorithmParameters to reflect saved values
          this.originalAlgorithmParameters = JSON.parse(JSON.stringify(this.algorithmParameters))

          this.updatePreview()
          this.unsavedParameterChanges = false

          this.$notify({
            group: 'alerts',
            type: 'success',
            title: this.$t('views.switch.alarmsettings.alarmsettingssaved'),
            duration: 1000,
            speed: 1000
          })
        } else {
          this.$notify({
            group: 'alerts',
            type: 'success',
            title: this.$t('views.switch.alarmsettings.alarmsettingsalreadyup-to-date'),
            duration: 1000,
            speed: 1000
          })
        }

        // Update start date
        if (this.startDate.getTime() !== this.originalStartDate.getTime()) {
          await this.saveStartDateAsync()
          this.originalStartDate = this.startDate
        } else {
          this.$notify({
            group: 'alerts',
            type: 'success',
            title: this.$t('views.switch.alarmsettings.startdatealreadyup-to-date'),
            duration: 1000,
            speed: 1000
          })
        }
      } catch (e) {
        this.$notify({
          group: 'alerts',
          type: 'error',
          title: this.$t('views.switch.alarmsettings.alarmsettingsnotsaved'),
          text: '' + e,
          duration: 1000,
          speed: 1000
        })
      }
    },

    async saveStartDateAsync () {
      try {
        const date = new Date(this.startDate)
        const userTimezoneOffset = date.getTimezoneOffset() * 60000 // offset in milliseconds

        // Adjust date to account for timezone offset
        const adjustedDate = new Date(date.getTime() - userTimezoneOffset)
        await PointAlarmsPrototypeService.updateStartDate(this.selectedAsset, this.r8token, adjustedDate)
        this.assetWithAlgorithms.startDate = this.startDate

        this.$notify({
          group: 'alerts',
          type: 'success',
          title: this.$t('views.switch.alarmsettings.startdatesaved'),
          duration: 1000,
          speed: 1000
        })
      } catch (e) {
        this.$notify({
          group: 'alerts',
          type: 'error',
          title: this.$t('views.switch.alarmsettings.startdatenotsaved'),
          text: '' + e,
          duration: 1000,
          speed: 1000
        })
      }
    },

    updatePreview () {
      this.refreshPreview = !this.refreshPreview
      this.samples = []
      this.areaRangeData = []
    },

    pointSelected (samples, color, direction, areaRangeData, title) {
      this.samples = samples
      this.color = color
      this.plotDirection = direction
      this.areaRangeData = areaRangeData
      this.title = title
    },

    checkUnsavedChanges () {
      this.unsavedParameterChanges = (JSON.stringify(this.algorithmParameters) !== JSON.stringify(this.originalAlgorithmParameters) || this.startDate.getTime() !== this.originalStartDate.getTime())
    },

    setError (message) {
      this.serverError = message
    },

    setInformation (message) {
      this.information = message
    },

    handleDirectionChange (event) {
      const newDirection = event.target.value

      // Show the confirmation modal if switching from 'Both' to 'Normal' or 'Reverse'
      if (this.selectedDirection === 'Both' && (newDirection === 'Normal' || newDirection === 'Reverse')) {
        this.newDirection = newDirection
        this.showConfirmationModal = true
      } else {
        // Trigger watcher and update direction
        this.previousDirection = this.selectedDirection
        this.proceedWithDirectionChange = true
        this.selectedDirection = newDirection
      }
    },

    resetSectionsAndDirection () {
      this.sections = []
      this.selectedSections = []
      this.tempSelectedSections = []
      this.selectedDirection = 'Both'
      this.newDirection = 'Both'
      this.disableBoth = false
    },

    confirmDirectionChange () {
      // Confirm and apply the new direction
      this.previousDirection = this.selectedDirection
      this.proceedWithDirectionChange = true
      this.selectedDirection = this.newDirection
      this.showConfirmationModal = false
      this.newDirection = this.selectedDirection
    },

    cancelDirectionChange () {
      // Cancel the change and revert to the previous direction
      this.showConfirmationModal = false
      this.newDirection = this.previousDirection
    },

    openActivateAlgorithmModal () {
      this.tempSelectedSections = JSON.parse(JSON.stringify(this.selectedSections))
      this.$nextTick(() => {
        this.showActivateAlgorithmModal = true
      })
    },

    openEditSectionsModal () {
      this.tempSelectedSections = JSON.parse(JSON.stringify(this.selectedSections))
      this.$nextTick(() => {
        this.showEditSectionsModal = true
      })
    },

    async confirmActivateAlgorithmSelectionModal (selectedAlgorithm) {
      this.activating = true

      try {
        this.selectedSections = JSON.parse(JSON.stringify(this.tempSelectedSections))
        await this.activateAlgorithmAsync(selectedAlgorithm)
      } catch (error) {
        this.setError('Error activating algorithm:', error)
      } finally {
        this.activating = false
        this.showActivateAlgorithmModal = false
      }
    },

    async confirmEditSectionsModal () {
      this.activating = true
      try {
        const assetAlgorithms = this.assetWithAlgorithms.assetAlgorithms.filter(
          (x) => x.algorithm.id === this.selectedAlgorithm.id
        )

        // Sanity check
        if (assetAlgorithms === null || assetAlgorithms.length === 0) {
          throw new Error('AssetAlgorithm is missing')
        }

        // Extract section types
        this.selectedSections = JSON.parse(JSON.stringify(this.tempSelectedSections))
        let sectionTypes = []
        this.selectedSections.forEach(s => {
          sectionTypes.push(
            s.sectionType
          )
        })

        for (const assetAlgorithm of assetAlgorithms) {
          await PointAlarmsPrototypeService.updateAlgorithmSections(this.selectedAsset, this.r8token, {
            assetAlgorithmId: assetAlgorithm.id,
            sectionTypes: sectionTypes
          })
        }

        // Select default for preview
        if (this.selectedSections.length > 0) {
          let defaultValue = this.selectedSections.find(section => section.isDefault)
          if (defaultValue) {
            this.selectedSection = defaultValue.sectionType
          } else {
            this.selectedSection = this.selectedSections[0].sectionType
          }
        }

        await this.selectAlgorithmAsync(this.selectedAlgorithm, true)

        this.$notify({
          group: 'alerts',
          type: 'success',
          title: 'Sections updated',
          duration: 1000,
          speed: 1000
        })
      } catch (err) {
        this.setError(err)
        this.tempSelectedSections = []
        this.$notify({
          group: 'alerts',
          type: 'error',
          title: 'Sections not updated',
          text: '' + err.response.data.title,
          duration: 1000,
          speed: 1000
        })
      } finally {
        this.showEditSectionsModal = false
        this.activating = false
      }
    },

    cancelActivateAlgorithmSelectionModal () {
      this.tempSelectedSections = []
      this.showActivateAlgorithmModal = false
    },

    cancelEditSectionsModal () {
      this.tempSelectedSections = []
      this.showEditSectionsModal = false
    },

    isTempSelected (section) {
      return this.tempSelectedSections.some(
        selected => selected.sectionType === section.sectionType
      )
    },

    toggleTempSelection (section) {
      const existingIndex = this.tempSelectedSections.findIndex(
        selected => selected.sectionType === section.sectionType
      )

      if (existingIndex >= 0) {
        this.tempSelectedSections.splice(existingIndex, 1)
      } else {
        const newSection = {
          id: section.value,
          sectionType: section.sectionType,
          name: section.name
        }
        this.tempSelectedSections.push(newSection)
      }
    },

    toggleIgnoreOutliers () {
      this.ignoreOutliers = !this.ignoreOutliers
    }
  }
}
</script>

<style scoped>
.selected {
  background-color: lightcyan;
}

.algorithms td {
  padding: 2px 5px;
}

.algorithmListItem {
  font-size: 13px;
  color: black !important;
}

select {
  height: 2.5rem;
}

.dropdown-container {
  margin-top: 10px;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
}

.dropdown-container-small {
  margin-top: 0px;
  margin-bottom: 0px;
  margin-left: 0px;
  margin-right: 0px;
  display: flex;
  align-items: center;
}

.custom-select {
  position: relative;
  width: 100%;
  text-align: left;
  outline: none;
  height: 47px;
  line-height: 47px;
}

.custom-select .selected {
  background-color: #FFFFFF;
  border-radius: 3px;
  border: 1px solid #B6B6B6;
  color: #000000;
  padding-left: 1em;
  cursor: pointer;
  user-select: none;
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-select .selected.open {
  border: 1px solid #B6B6B6;
  border-radius: 3px 3px 3px 3px;
}

.custom-select .selected:after {
  position: absolute;
  content: "";
  top: 22px;
  right: 1em;
  width: 0;
  height: 0;
  border: 5px solid transparent;
  border-color: #000000 transparent transparent transparent;
}

.custom-select .items {
  color: #fff;
  border-radius: 0px 0px 6px 6px;
  overflow: hidden;
  border-right: 1px solid #B6B6B6;
  border-left: 1px solid #B6B6B6;
  border-bottom: 1px solid #B6B6B6;
  position: absolute;
  background-color: #0A0A0A;
  left: 0;
  right: 0;
  z-index: 1000;
}

.custom-select .items .item-option {
  background-color: #FFFFFF;
  color: #000000;
  padding-left: 1em;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
}

.custom-select .items .item-option:hover {
  background-color: #EEEEEE;
}

.selectHide {
  display: none;
}

.dropdown-label {
  font-weight: bold;
  margin-right: 1em;
}

.materialert {
    position: relative;
    min-width: 150px;
    padding: 5px;
    margin-bottom: 5px;
    margin-top: 5px;
    border: 1px solid transparent;
    border-radius: 4px;
    transition: all 0.1s linear;
    box-shadow: 0 2px 2px 0 rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12), 0 1px 5px 0 rgba(0,0,0,0.2);
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -webkit-align-items: center;
    -ms-flex-align: center;
    align-items: center;
}

.materialert .material-icons {
    margin-right: 10px;
}

.materialert.info {
    background-color: #039be5;
    color: #fff;
}

.materialert.success {
    background-color: #43a047;
    color: #fff;
}

.materialert.error {
    background-color: #c62828;
    color: #fff;
}

.materialert.danger {
    background-color: #c62828;
    color: #fff;
}

.materialert.warning {
    background-color: #fbc02d;
    color: #000000;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  width: 500px;
  max-width: 90%;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.modal-footer button {
  margin-left: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
}
</style>



// WEBPACK FOOTER //
// src/pages/switch/AlarmConfiguration.vue